<script setup lang="ts">
import Loader from "~/components/Common/Loader.vue";
import SvgIcon from "~/components/Common/SvgIcon.vue";
import { useTrainingPrefillStore } from "~/store/copilot/training-prefill.store";
import { withCollapsedPdfNavigationPane } from "~/utils/helpers/pdf-viewer";
import { safeExternalUrl } from "~/utils/helpers/sanitize";

/**
 * The viewer takes a share of the modal rather than a fixed width, so it follows the window; the
 * edge can be dragged to change that share, within bounds that keep both the PDF and the form usable.
 */
const DEFAULT_VIEWER_RATIO = 0.5;
const MIN_VIEWER_RATIO = 0.3;
const MAX_VIEWER_RATIO = 0.7;

const prefillStore = useTrainingPrefillStore();

/* Same rendering as the LMS document player: the browser's own viewer inside an iframe. */
const isLoading = ref(true);

/** The URL shown in the iframe: a source web page when importing from a URL, otherwise the stored PDF. */
const viewerUrl = computed(() => {
  const url = prefillStore.sourceUrl ?? prefillStore.document?.url ?? null;
  if (!url) return null;
  return safeExternalUrl(import.meta.client ? new URL(url, window.location.origin).href : url) ?? null;
});

/** A submitted web page keeps its own fragment; only the embedded PDF gets native-viewer options. */
const embeddedViewerUrl = computed(() =>
  prefillStore.sourceUrl ? viewerUrl.value : withCollapsedPdfNavigationPane(viewerUrl.value),
);

// Folding the panel no longer unmounts the iframe, so a new source has to put the loader back.
watch(embeddedViewerUrl, () => {
  isLoading.value = true;
});

const viewerLabel = computed(() => {
  if (prefillStore.sourceUrl) {
    try {
      return new URL(prefillStore.sourceUrl).hostname;
    } catch {
      return "";
    }
  }
  return prefillStore.document?.fileName ?? "";
});

/** Session-scoped: a share chosen once survives toggling the viewer and the next import. */
const viewerRatio = useState<number>("training_program_viewer_ratio", () => DEFAULT_VIEWER_RATIO);
const asideRef = ref<HTMLElement | null>(null);
const isResizing = ref(false);

const widthStyle = computed(() => ({ width: `${viewerRatio.value * 100}%` }));

function startResize(event: MouseEvent) {
  const aside = asideRef.value;
  const containerWidth = aside?.parentElement?.getBoundingClientRect().width;
  if (!aside || !containerWidth) return;

  const startX = event.clientX;
  const startWidth = aside.getBoundingClientRect().width;
  isResizing.value = true;

  const onMove = (move: MouseEvent) => {
    const ratio = (startWidth + move.clientX - startX) / containerWidth;
    viewerRatio.value = Math.min(Math.max(ratio, MIN_VIEWER_RATIO), MAX_VIEWER_RATIO);
  };
  const onUp = () => {
    isResizing.value = false;
    window.removeEventListener("mousemove", onMove);
    window.removeEventListener("mouseup", onUp);
  };

  window.addEventListener("mousemove", onMove);
  window.addEventListener("mouseup", onUp);
}
</script>

<template>
  <aside
    ref="asideRef"
    class="relative flex h-full min-w-[400px] shrink-0 flex-col border-r border-stroke-color bg-canvas-grey-color"
    :style="widthStyle"
  >
    <header class="flex h-10 shrink-0 items-center gap-2 border-b border-stroke-color bg-light-color px-4">
      <SvgIcon name="document" class="h-[16px] w-[16px] shrink-0 text-secondary-color" />
      <span class="body_regular truncate text-primary-color" :title="viewerLabel">
        {{ viewerLabel }}
      </span>

      <button
        type="button"
        class="ml-auto shrink-0 cursor-pointer text-secondary-color"
        aria-label="Masquer le programme"
        title="Masquer le programme"
        @click="prefillStore.toggleViewer()"
      >
        <SvgIcon name="arrow_back" class="h-[16px] w-[16px]" />
      </button>
    </header>

    <div class="relative flex-1">
      <div v-if="isLoading" class="absolute inset-0 flex items-center justify-center">
        <Loader :width="32" :height="32" />
      </div>
      <!-- While dragging, the iframe must not swallow the mouse events the resize listens to. -->
      <iframe
        v-if="embeddedViewerUrl"
        :src="embeddedViewerUrl"
        title="Programme de formation"
        width="100%"
        height="100%"
        frameborder="0"
        :class="{ 'pointer-events-none': isResizing }"
        @load="isLoading = false"
      ></iframe>
    </div>

    <!-- Drag handle on the edge shared with the form. -->
    <div
      class="absolute inset-y-0 -right-1 z-10 w-2 cursor-col-resize hover:bg-light-blue-color"
      :class="{ 'bg-light-blue-color': isResizing }"
      aria-hidden="true"
      @mousedown.prevent="startResize"
    />
  </aside>
</template>
